import {
  BoxGeometry,
  CylinderGeometry,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
} from "three";
import type { CarParams } from "@midnight-pass/sim";

const BODY_COLOUR = "#2a63ff";
const CABIN_COLOUR = "#141a33";
const WHEEL_COLOUR = "#0b0d14";
const SPOKE_COLOUR = "#c8d0ee";
const WHEEL_WIDTH = 0.24;
const RIDE_HEIGHT = 0.18;

export interface CarModel {
  group: Group;
  frontWheels: Group[];
  /** Meshes that spin about the axle, in the same order as the four wheels (FL, FR, RL, RR). */
  spinners: Mesh[];
  dispose(): void;
}

/**
 * A box-shaped car with four wheel meshes that steer (front) and spin. Origin is the CoG on the
 * ground; +z is forward and +x is left, matching the sim's body frame.
 */
export function buildCarModel(car: CarParams): CarModel {
  const group = new Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    disposables.push(x);
    return x;
  };

  const a = car.wheelbase * (1 - car.frontWeight); // CoG to front axle
  const b = car.wheelbase * car.frontWeight; // CoG to rear axle
  const bodyH = 0.5;
  const bodyY = RIDE_HEIGHT + car.wheelRadius * 0.6 + bodyH / 2;

  const bodyGeo = track(new BoxGeometry(car.width, bodyH, car.length * 0.92));
  const body = new Mesh(bodyGeo, track(new MeshBasicMaterial({ color: BODY_COLOUR })));
  body.position.y = bodyY;
  body.add(
    new LineSegments(
      track(new EdgesGeometry(bodyGeo)),
      track(new LineBasicMaterial({ color: "#00f0ff" })),
    ),
  );
  group.add(body);

  const cabinGeo = track(new BoxGeometry(car.width * 0.82, 0.42, car.length * 0.42));
  const cabin = new Mesh(cabinGeo, track(new MeshBasicMaterial({ color: CABIN_COLOUR })));
  cabin.position.set(0, bodyY + bodyH / 2 + 0.21, -car.length * 0.06);
  cabin.add(
    new LineSegments(
      track(new EdgesGeometry(cabinGeo)),
      track(new LineBasicMaterial({ color: "#00f0ff" })),
    ),
  );
  group.add(cabin);

  // Lights make the heading obvious: warm at the front, red at the back.
  const lightGeo = track(new BoxGeometry(0.32, 0.12, 0.06));
  const headMat = track(new MeshBasicMaterial({ color: "#fff2b0" }));
  const tailMat = track(new MeshBasicMaterial({ color: "#ff184c" }));
  for (const side of [1, -1]) {
    const head = new Mesh(lightGeo, headMat);
    head.position.set(side * car.width * 0.32, bodyY + 0.05, car.length * 0.46);
    const tail = new Mesh(lightGeo, tailMat);
    tail.position.set(side * car.width * 0.32, bodyY + 0.05, -car.length * 0.46);
    group.add(head, tail);
  }

  const wheelGeo = track(new CylinderGeometry(car.wheelRadius, car.wheelRadius, WHEEL_WIDTH, 14));
  wheelGeo.rotateZ(Math.PI / 2); // axle along x
  const wheelMat = track(new MeshBasicMaterial({ color: WHEEL_COLOUR }));
  const spokeGeo = track(new BoxGeometry(WHEEL_WIDTH + 0.02, car.wheelRadius * 1.7, 0.06));
  const spokeMat = track(new MeshBasicMaterial({ color: SPOKE_COLOUR }));

  const frontWheels: Group[] = [];
  const spinners: Mesh[] = [];
  const half = car.width / 2 - WHEEL_WIDTH / 2 + 0.03;
  const layout: [number, number, boolean][] = [
    [half, a, true],
    [-half, a, true],
    [half, -b, false],
    [-half, -b, false],
  ];
  for (const [x, z, front] of layout) {
    const pivot = new Group();
    pivot.position.set(x, car.wheelRadius, z);
    const wheel = new Mesh(wheelGeo, wheelMat);
    wheel.add(new Mesh(spokeGeo, spokeMat));
    pivot.add(wheel);
    group.add(pivot);
    spinners.push(wheel);
    if (front) frontWheels.push(pivot);
  }

  return {
    group,
    frontWheels,
    spinners,
    dispose: () => disposables.forEach((d) => d.dispose()),
  };
}
